<template>
  <v-form
    ref="form"
    v-model="isValidationOk"
    lazy-validation>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        wrap>
        <v-flex
          xs12
          md12
          lg12
          text-xs-center>
          <h6
            data-testId="label-ACR-configuration"
            class="heading-6 text-uppercase">
            {{ $t('ACR configuration') }}
          </h6>
          <hr>
        </v-flex>
        <v-flex
          xs12
          md4>
          <v-text-field
            v-model="tank.unstableDeliveryTime"
            :counter="6"
            :rules="rules.required.concat(rules.positive)"
            :label="`${ $t('Unstable delivery time ({0})', [ $t('min') ]) }`"
            data-testId="text-field-Unstable-delivery-time"
            mask="#####"
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs12
          md4>
          <v-text-field
            v-model="tank.deliveryStarting"
            :counter="6"
            :rules="rules.required.concat(rules.positive)"
            :label="`${ $t('Start delivery detection') + ' (' + [ options.volumeUnits[systemSettings.volumeUnit || 0].desc ] + ')' }`"
            data-testId="text-field-Start-delivery-detection"
            mask="#####"
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs12
          md4>
          <v-text-field
            v-model="tank.deliveryEnding"
            :counter="6"
            :rules="rules.required.concat(rules.positive)"
            :label="`${ $t('Ending delivery detection') + ' (' + [ options.volumeUnits[systemSettings.volumeUnit || 0].desc ] + '/' + $t('min') + ')' }`"
            data-testId="text-field-Ending-delivery-detection"
            mask="#####"
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs12
          md4>
          <v-select
            v-model="tank.vaporRecoveryType"
            :items="vaporSystems"
            :label="`${ $t('Vapor Recovery System') }`"
            data-testId="select-Vapor-Recovery-System"
            item-text="desc"
            item-value="index"
            class="dover-input"
            chips>
            <template
              slot="selection"
              slot-scope="data">
              <v-chip
                :selected="data.selected"
                :disabled="data.disabled"
                :key="data.item.index"
                data-testId="chip"
                class="v-chip--select-multi "
                @input="data.parent.selectItem(data.item)">
                <v-avatar class="dover-avatar ">{{ data.item.index }}</v-avatar>
                {{ data.item.desc }}
              </v-chip>
            </template>
          </v-select>
        </v-flex>
        <v-flex
          xs12
          md4>
          <v-text-field
            v-model.trim="tank.evaporationFactor"
            :counter="6"
            :rules="rules.required.concat(rules.perc)"
            :label="`${ $t('Evaporation factor (%)') }`"
            :disabled="features.weightsMeasuresLock"
            data-testId="text-field-Evaporation-factor"
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs12
          md12
          lg12
          text-xs-center>
          <h6
            data-testId="label-Calibration-parameters"
            class="heading-6 text-uppercase">
            {{ $t('Calibration parameters') }}
          </h6>
          <hr>
        </v-flex>
        <v-flex
          xs12
          md12
          lg12>
          <span
            data-testId="span-Calibration-segments"
            class="caption grey--text font-weight-bold">
            {{ $t('Calibration segments') }}
          </span>
          <hr>
        </v-flex>
        <v-flex
          xs12
          md4>
          <v-text-field
            v-model="numberOfCalibrationSegments"
            :counter="2"
            :rules="rules.required.concat(rules.segment)"
            :label="`${ $t('Number of segments ({0})', [ '25-45' ]) }`"
            data-testId="text-field-Number-of-segments"
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs6
          md4>
          <v-text-field
            v-model="lowCalibrationSegment"
            :counter="2"
            :rules="rules.required.concat(rules.lowSegment)"
            :label="`${ $t('Low segment') }`"
            data-testId="text-field-Low-segment"
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs6
          md4>
          <v-text-field
            v-model="highCalibrationSegment"
            :counter="2"
            :rules="rules.required.concat(rules.highSegment)"
            :label="`${ $t('High segment') }`"
            data-testId="text-field-High-segment"
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs12
          md12
          lg12>
          <span
            data-testId="span-Calibration-cycles"
            class="caption grey--text font-weight-bold">
            {{ $t('Calibration cycles') }}
          </span>
          <hr>
        </v-flex>
        <v-flex
          xs6
          md6
          text-xs-left>
          <v-text-field
            v-model="maxCalibrationCycles"
            :counter="3"
            :rules="rules.required.concat(rules.maxCycle)"
            :label="`${ $t('Max') }`"
            data-testId="text-field-Max"
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs6
          md6
          text-xs-right>
          <v-text-field
            v-model="requiredCalibrationCycles"
            :counter="3"
            :rules="rules.required.concat(rules.requiredCycle)"
            :label="`${ $t('Required') }`"
            data-testId="text-field-Required"
            class="dover-input"/>
        </v-flex>
        <v-container>
          <v-layout justify-row-center>
            <MaglinkComponentDialogYesNo
              :key="dialogYN.key"
              :component-key="dialogYN.componentKey"
              :dialog="dialogYN.show"
              :title="dialogYN.title"
              :label-text="dialogYN.labelText"
              data-testId="MaglinkComponentDialogYesNo-dialogYN"
              @dialogResultYesNo="onSaveDialogResult"/>
            <v-flex
              xs12
              md12
              text-xs-right>
              <v-btn
                :disabled="!isValidationOk || !Maglink.IsAdminUser()"
                data-testId="btn-Save"
                small
                class="mx-0 font-weight-light"
                color="dover-blue"
                @click="saveTank">
                {{ $t('Save') }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-container>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
import Vue from 'vue'
import { i18n } from '@/i18n'

export default {
  props: {
    tank: {
      type: Object,
      required: true,
      default: () => {}
    },
    systemSettings: {
      type: Object,
      required: true,
      default: () => {}
    },
    options: {
      type: Object,
      required: true,
      default: () => {}
    },
    features: {
      type: Object,
      required: true,
      default: () => {}
    }
  },
  data: () => ({
    isValidationOk: false,
    vaporSystems: [
      { index: 0, desc: i18n.t('No Vapor Recovery System') },
      { index: 1, desc: i18n.t('Stage 1 VR') },
      { index: 2, desc: i18n.t('Stage 2 VR') },
      { index: 3, desc: i18n.t('Stage 1 and stage 2 VR') },
      { index: 4, desc: i18n.t('TVS') }
    ],
    rules: {
      required: [ v => Vue.prototype.Maglink.RulesRequired(v) ],
      perc: [ v => Vue.prototype.Maglink.RulesRange(v, 0, 100) ],
      maxCycle: [ v => Vue.prototype.Maglink.RulesRange(v, 1, 100) ],
      requiredCycle: [ v => Vue.prototype.Maglink.RulesRange(v, 1, 100) ],
      positive: [ v => Vue.prototype.Maglink.RulesRange(v, 0, 100) ],
      segment: [ v => Vue.prototype.Maglink.RulesRange(v, 25, 45) ],
      lowSegment: [ v => Vue.prototype.Maglink.RulesRange(v, 1, 20) ],
      highSegment: [ v => Vue.prototype.Maglink.RulesRange(v, 1, 20) ]
    },
    numberOfCalibrationSegments: 25,
    lowCalibrationSegment: 1,
    highCalibrationSegment: 25,
    requiredCalibrationCycles: 1,
    maxCalibrationCycles: 1,
    criticalChange: false,
    initialNumberOfCalibrationSegments: 25,
    dialogYN: {
      key: 0,
      show: false,
      componentKey: 'criticalChange',
      title: '',
      labelText: '',
      result: ''
    }
  }),
  watch: {
    numberOfCalibrationSegments (newVal, oldVal) {
      this.criticalChange = (newVal * 1) !== this.initialNumberOfCalibrationSegments
      this.segmentsCheck()
    },
    lowCalibrationSegment (newVal, oldVal) {
      this.segmentsCheck()
    },
    highCalibrationSegment (newVal, oldVal) {
      this.segmentsCheck()
    },
    requiredCalibrationCycles (newVal, oldVal) {
      this.cyclesCheck()
    },
    maxCalibrationCycles (newVal, oldVal) {
      this.cyclesCheck()
    }
  },
  mounted () {
    let item = this.tank
    this.lowCalibrationSegment = item.lowCalibrationSegment
    this.highCalibrationSegment = item.highCalibrationSegment
    this.numberOfCalibrationSegments = item.numberOfCalibrationSegments
    this.initialNumberOfCalibrationSegments = item.numberOfCalibrationSegments
    this.requiredCalibrationCycles = item.requiredCalibrationCycles
    this.maxCalibrationCycles = item.maxCalibrationCycles

    this.segmentsCheck()
  },
  methods: {
    segmentsCheck () {
      let totalSegments = this.numberOfCalibrationSegments * 1
      this.rules.lowSegment = this.rules.required.concat(Vue.prototype.Maglink.RulesRange(this.lowCalibrationSegment * 1, 1, totalSegments))
      this.rules.highSegment = this.rules.required.concat(Vue.prototype.Maglink.RulesRange(this.highCalibrationSegment * 1, this.lowCalibrationSegment * 1, totalSegments))
      this.$refs.form.validate()
    },
    cyclesCheck () {
      this.rules.requiredCycle = this.rules.required.concat(Vue.prototype.Maglink.RulesRange(this.requiredCalibrationCycles * 1, 1, this.maxCalibrationCycles * 1))
      this.$refs.form.validate()
    },
    saveTank (event) {
      if (this.$refs.form.validate()) {
        this.isValidationOk = true

        if (this.criticalChange) {
          this.dialogYN.title = this.$t('Warning').toUpperCase()
          this.dialogYN.labelText = '\n' + this.$t('You are about to save changes to the tank configuration, if you continue some temporary data will be destroyed.') + '\n\n' + this.$t('Are you sure?' + '\n')
          this.dialogYN.result = this.tank.tankNumber.toString()
          this.dialogYN.show = true
        } else {
          // No critical change, go straight to save
          this.onSaveDialogResult('criticalChange', true)
        }
      } else {
        this.isValidationOk = false
      }
    },
    onSaveDialogResult (componentKey, result) {
      this.dialogYN.show = false
      // NO button pressed
      if (!result) return

      if (this.criticalChange) {
        this.tank.serialize = Vue.prototype.Maglink.generateSerialize(this.tank.serialize)
      }
      let item = this.tank
      // used local var to manage watch
      item.lowCalibrationSegment = this.lowCalibrationSegment * 1
      item.highCalibrationSegment = this.highCalibrationSegment * 1
      item.numberOfCalibrationSegments = this.numberOfCalibrationSegments * 1
      item.requiredCalibrationCycles = this.requiredCalibrationCycles * 1
      item.maxCalibrationCycles = this.maxCalibrationCycles * 1

      item.evaporationFactor = item.evaporationFactor * 1
      item.vaporRecoveryType = item.vaporRecoveryType * 1
      item.deliveryEnding = item.deliveryEnding * 1
      item.deliveryStarting = item.deliveryStarting * 1
      item.unstableDeliveryTime = item.unstableDeliveryTime * 1

      this.$emit('tankSaved', this.tank.tankNumber)
    },
    snack (color, message) {
      message = this.$t('Tank ACR settings') + ': ' + message
      this.$store.dispatch('showSnackbar', { color, message, show: true })
    }
  }
}
</script>
